/* FOB Prices module — forecast layer (Sub-fase D.2 / Fase A del roadmap
   "FOB PRICE FORECAST v2" en CLAUDE.md). Kept in its own file (no UI),
   same convention as fobPricesCalc.js / saleStatusCalc.js / profitCalc.js.

   2026-09-12 — historical_prices (USDA + SNIIM) hoy solo tiene datos de
   2026 YTD, muy por debajo de las ~104 semanas (2 ciclos de 52) que hacen
   falta para ajustar una estacionalidad real. En vez de esperar al
   backfill de 7 años (tarea aparte, aun sin empezar) para escribir este
   archivo, el forecast usa 2 niveles:
   - Tier 1 (hoy, con los datos reales que ya existen): Holt lineal
     (nivel + tendencia, sin estacionalidad) — "trend-only".
   - Tier 2 (automatico en cuanto haya suficiente historia en la misma
     tabla, sin tocar este codigo): Holt-Winters aditivo completo
     (nivel + tendencia + estacionalidad) — "seasonal".
   El caller nunca elige el tier; se decide solo segun cuantos puntos
   reales hay. Ver holtWintersForecast() abajo. */

/* Same ISO-8601 week algorithm as getISOWeek() in fobPricesCalc.js /
   SalesReports.js / BillRegistry.js / etc. — duplicated here rather than
   imported, per this codebase's per-file convention (CLAUDE.md), kept
   byte-identical so weeks line up with every other module's numbering. */
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 864e5 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}

/* Same isoYear+isoWeek extraction as getISOWeek() above, but also returns
   the (possibly rolled-over) ISO year — needed to label forecast points
   that cross a year boundary (e.g. week 51 2026 + 3 weeks -> week 2 2027). */
function isoWeekYearInfo(date) {
  var d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var isoYear = d.getFullYear();
  var w1 = new Date(isoYear, 0, 4);
  var isoWeek = 1 + Math.round(((d - w1) / 864e5 - 3 + ((w1.getDay() + 6) % 7)) / 7);
  return { isoYear: isoYear, isoWeek: isoWeek };
}

/* Monday of ISO week 1 of `year` — Jan 4 always falls in week 1, so its
   own Monday anchors the whole ISO week calendar for that year. */
function mondayOfIsoWeek1(year) {
  var jan4 = new Date(year, 0, 4);
  var dow = (jan4.getDay() + 6) % 7; // Mon=0..Sun=6
  var monday = new Date(jan4.getTime());
  monday.setDate(jan4.getDate() - dow);
  return monday;
}

/* Adds `weeksToAdd` ISO weeks to a (year, "W23") label and returns the
   resulting { year, weekIso } — used to generate future labels for the
   forecast rows, since usdaSeries only carries (year, weekIso), not a
   real date. */
function addIsoWeeks(year, weekIsoStr, weeksToAdd) {
  var weekNum = parseInt(String(weekIsoStr).replace(/\D/g, ""), 10) || 1;
  var monday1 = mondayOfIsoWeek1(year);
  var target = new Date(monday1.getTime());
  target.setDate(monday1.getDate() + (weekNum - 1 + weeksToAdd) * 7);
  var info = isoWeekYearInfo(target);
  return { year: info.isoYear, weekIso: "W" + String(info.isoWeek).padStart(2, "0") };
}

function average(arr) {
  if (!arr.length) return 0;
  return arr.reduce(function (s, v) { return s + v; }, 0) / arr.length;
}

function stdDev(arr) {
  if (!arr.length) return 0;
  var m = average(arr);
  var variance = average(arr.map(function (v) { return (v - m) * (v - m); }));
  return Math.sqrt(variance);
}

function round2(n) { return Math.round(n * 100) / 100; }

/* Tier 1 — Holt's linear trend method (double exponential smoothing, no
   seasonal term). alpha/beta are fixed, documented defaults rather than
   fitted/optimized — with under a year of real data there isn't enough
   history to grid-search these meaningfully yet; revisit once the
   backfill lands and there's enough data to validate a tuned choice. */
var HOLT_ALPHA = 0.3;
var HOLT_BETA = 0.1;
function fitHoltLinear(values) {
  var level = values[0];
  var trend = values.length > 1 ? values[1] - values[0] : 0;
  var residuals = [];
  for (var t = 1; t < values.length; t++) {
    var prevLevel = level, prevTrend = trend;
    residuals.push(values[t] - (prevLevel + prevTrend));
    level = HOLT_ALPHA * values[t] + (1 - HOLT_ALPHA) * (prevLevel + prevTrend);
    trend = HOLT_BETA * (level - prevLevel) + (1 - HOLT_BETA) * prevTrend;
  }
  return { level: level, trend: trend, season: null, residuals: residuals };
}

/* Tier 2 — additive Holt-Winters (triple exponential smoothing). Additive
   (not multiplicative) seasonality: price moves by a seasonal +/- $
   amount, not a seasonal ratio — matches how USDA/SNIIM report a price
   range rather than an index, and avoids blowing up at low prices.
   alpha/beta/gamma are fixed defaults for the same reason as Tier 1. Only
   called once the caller has confirmed >= 2*seasonPeriod points and >= 2
   distinct years (see holtWintersForecast's sufficiency gate) — this
   function assumes that's already true and does not re-check it. */
var HW_ALPHA = 0.3;
var HW_BETA = 0.1;
var HW_GAMMA = 0.2;
function fitHoltWinters(values, seasonPeriod) {
  var season1 = values.slice(0, seasonPeriod);
  var season2 = values.slice(seasonPeriod, 2 * seasonPeriod);
  var avg1 = average(season1);
  var avg2 = average(season2);
  var level = avg1;
  var trend = (avg2 - avg1) / seasonPeriod;
  var season = season1.map(function (v) { return v - avg1; });
  var residuals = [];
  for (var t = seasonPeriod; t < values.length; t++) {
    var seasonIdx = t % seasonPeriod;
    var prevLevel = level, prevTrend = trend, prevSeason = season[seasonIdx];
    residuals.push(values[t] - (prevLevel + prevTrend + prevSeason));
    level = HW_ALPHA * (values[t] - prevSeason) + (1 - HW_ALPHA) * (prevLevel + prevTrend);
    trend = HW_BETA * (level - prevLevel) + (1 - HW_BETA) * prevTrend;
    season[seasonIdx] = HW_GAMMA * (values[t] - level) + (1 - HW_GAMMA) * prevSeason;
  }
  return { level: level, trend: trend, season: season, residuals: residuals };
}

/* usdaSeries: weekly rows shaped like FobPricesRegistry.js's own
   `periodRows` (the same variable it already passes to this function) —
   { year, weekIso, priceLow, priceHigh, ... other fields ignored }. Also
   accepts the output of aggregateHistoricalPricesByWeek() below, which
   produces the same { year, weekIso, priceLow, priceHigh } shape on
   purpose so both callers share one contract.

   options:
   - horizon (default 8) — weeks forward to project.
   - seasonPeriod (default 52) — weekly cycle length; only actually used
     once the sufficiency gate passes, see below.
   - granularityKey (default "") — caller-supplied label for the product/
     category/size cell being forecast, echoed back on the result for
     display/debugging. This function does not itself split by
     granularity — the caller pre-filters usdaSeries.
   - internalSeries — accepted for forward compatibility (matches the
     original stub's documented intent, GFI's own weekly price series
     from fobPricesCalc.js's aggregateInternalPricesByWeek) but NOT used
     to compute a second projection yet — that's future scope once there
     is real internal historical data to project (see Arturo's own
     10-year client/supplier price import, in progress separately).

   Sufficiency gate: seasonal (Tier 2) requires >= 2*seasonPeriod weekly
   points AND >= 2 distinct calendar years for this series — otherwise
   Tier 1 (trend-only) runs instead. This is not a stylistic choice: with
   under 2 full cycles there's no way to distinguish real seasonality from
   noise, and forcing a seasonal fit anyway would produce a confidently-
   wrong curve. Today's historical_prices (2026 YTD only) never clears
   this gate for any cell — that's expected, not a bug; the 7-year
   backfill (tracked separately) is what flips mode to "seasonal", with
   zero code changes needed here since it only adds more rows to the same
   table. */
export function holtWintersForecast(usdaSeries, options) {
  options = options || {};
  var horizon = options.horizon || 8;
  var seasonPeriod = options.seasonPeriod || 52;
  var granularityKey = options.granularityKey || "";

  var series = (usdaSeries || [])
    .filter(function (r) { return r && r.year != null && r.weekIso && r.priceLow != null && r.priceHigh != null; })
    .map(function (r) { return { year: r.year, weekIso: r.weekIso, mid: (parseFloat(r.priceLow) + parseFloat(r.priceHigh)) / 2 }; })
    .sort(function (a, b) {
      if (a.year !== b.year) return a.year - b.year;
      return a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0;
    });

  if (series.length < 3) {
    throw new Error("holtWintersForecast: need at least 3 weekly points to project, got " + series.length + ".");
  }

  var values = series.map(function (r) { return r.mid; });
  var distinctYears = {};
  series.forEach(function (r) { distinctYears[r.year] = true; });
  var yearCount = Object.keys(distinctYears).length;
  var useSeasonal = values.length >= (2 * seasonPeriod) && yearCount >= 2;

  var fit = useSeasonal ? fitHoltWinters(values, seasonPeriod) : fitHoltLinear(values);
  var sigma = stdDev(fit.residuals);

  var last = series[series.length - 1];
  var rows = [];
  for (var h = 1; h <= horizon; h++) {
    var label = addIsoWeeks(last.year, last.weekIso, h);
    var pointForecast;
    if (useSeasonal) {
      var seasonIdx = (values.length - 1 + h) % seasonPeriod;
      pointForecast = fit.level + h * fit.trend + fit.season[seasonIdx];
    } else {
      pointForecast = fit.level + h * fit.trend;
    }
    var band = 1.645 * sigma * Math.sqrt(h); // ~90% band, widening with horizon
    rows.push({
      year: label.year,
      weekIso: label.weekIso,
      forecast: round2(pointForecast),
      // A price can never go negative — the Gaussian band math doesn't
      // know that, so clamp only the lower bound here rather than
      // leaving a misleading negative price on the chart. The point
      // forecast itself is left alone (not clamped) so a genuinely
      // declining trend is still visible as such.
      low90: round2(Math.max(0, pointForecast - band)),
      high90: round2(pointForecast + band),
    });
  }

  return {
    granularityKey: granularityKey,
    mode: useSeasonal ? "seasonal" : "trend-only",
    seasonPeriod: useSeasonal ? seasonPeriod : null,
    pointsUsed: values.length,
    sufficiencyNote: useSeasonal
      ? null
      : ("pointsUsed:" + values.length + "|needed:" + (2 * seasonPeriod)),
    rows: rows,
  };
}

/* Buckets historical_prices rows (app-shape, via supabaseDB.js's
   TABLE_CONFIG.historical_prices mapper — source/product/category/
   packageSize/priceLow/priceHigh/reportDate) into ISO weeks, producing
   the { year, weekIso, priceLow, priceHigh } shape holtWintersForecast()
   consumes (same shape FobPricesRegistry.js's own periodRows already
   uses, so both callers share one contract). Averages priceLow/priceHigh
   independently across whatever reports land in the same week (mirrors
   FobPricesRegistry.js's own periodRows grouping). Never mixes sources —
   caller must pass rows already filtered to one `source` (USDA or SNIIM,
   never both at once, same currency-mixing rule as the rest of this
   module). Weeks with zero matching rows are omitted, not zero-filled —
   same "gap in the line, not a fake point" convention as
   aggregateInternalPricesByWeek() in fobPricesCalc.js. */
export function aggregateHistoricalPricesByWeek(historicalRows, source, productFilter, categoryFilter, sizeFilter) {
  var buckets = {};
  (historicalRows || []).forEach(function (r) {
    if (!r || r.source !== source) return;
    if (productFilter && r.product !== productFilter) return;
    if (categoryFilter && r.category !== categoryFilter) return;
    if (sizeFilter && r.packageSize !== sizeFilter) return;
    if (!r.reportDate) return;
    var year = new Date(r.reportDate + "T00:00:00").getFullYear();
    var weekIso = "W" + getISOWeek(r.reportDate);
    var key = year + "|" + weekIso;
    if (!buckets[key]) buckets[key] = { year: year, weekIso: weekIso, lows: [], highs: [] };
    if (r.priceLow != null) buckets[key].lows.push(parseFloat(r.priceLow));
    if (r.priceHigh != null) buckets[key].highs.push(parseFloat(r.priceHigh));
  });
  return Object.keys(buckets)
    .map(function (k) { return buckets[k]; })
    .filter(function (b) { return b.lows.length > 0 || b.highs.length > 0; })
    .map(function (b) {
      return {
        year: b.year,
        weekIso: b.weekIso,
        priceLow: b.lows.length ? round2(average(b.lows)) : null,
        priceHigh: b.highs.length ? round2(average(b.highs)) : null,
      };
    })
    .sort(function (a, b) {
      if (a.year !== b.year) return a.year - b.year;
      return a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0;
    });
}

/* Real (not mock) lb-per-truck conversion, given by Arturo 2026-09-15:
   a full truckload = 26 pallets of 240 cases, each case weighing 2.04 kg —
   26*240*2.04kg = 12,730.4 kg = 28,063.96 lb, rounded here. Replaces
   FobPricesRegistry.js's old MOCK_CASES_PER_TRUCK=1200 placeholder (that
   one was cases-per-truck for mock/synthetic data only, explicitly marked
   TODO, and was never wired to any real volume figure — trucksEquivalent
   on real USDA rows was always null). This constant is the one real
   figure both FobPricesRegistry.js's "Trucks equivalent" card and
   ForecastRegistry.js's truck-crossings overlay convert lb_units with. */
export var LBS_PER_TRUCK = 28064;

/* Buckets truck_crossings rows (app-shape — product/lbUnits/shipmentDate,
   via supabaseDB.js's TABLE_CONFIG.truck_crossings mapper) into ISO weeks,
   summing lbUnits — this is a VOLUME series (total pounds of Mexico truck
   crossings that week for the given product), not a price, so it's SUMMED
   per week rather than averaged like the price aggregators above. Added
   2026-09-15 alongside the truck_crossings ingest itself (CLAUDE.md Session
   Log) as a supply-context overlay for ForecastRegistry.js — not yet fed
   into holtWintersForecast() as an explanatory variable, just plotted
   alongside the price chart so Arturo can eyeball supply-vs-price
   correlation. Same "gap in the line, not a fake zero" convention as the
   price aggregators: a week with zero matching rows is omitted. */
export function aggregateTruckCrossingsByWeek(truckRows, productFilter) {
  var buckets = {};
  (truckRows || []).forEach(function (r) {
    if (!r || !r.shipmentDate) return;
    if (productFilter && r.product !== productFilter) return;
    var year = new Date(r.shipmentDate + "T00:00:00").getFullYear();
    var weekIso = "W" + getISOWeek(r.shipmentDate);
    var key = year + "|" + weekIso;
    if (!buckets[key]) buckets[key] = { year: year, weekIso: weekIso, total: 0 };
    if (r.lbUnits != null) buckets[key].total += parseFloat(r.lbUnits) || 0;
  });
  return Object.keys(buckets)
    .map(function (k) { return buckets[k]; })
    .map(function (b) { return { year: b.year, weekIso: b.weekIso, lbUnits: round2(b.total) }; })
    .sort(function (a, b) {
      if (a.year !== b.year) return a.year - b.year;
      return a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0;
    });
}

/* Buckets internal_price_history rows (app-shape — product/category/size/
   clientPrice/supplierPrice/reportDate) into ISO weeks. Unlike USDA/SNIIM,
   this is GFI's own transacted price — a single point value on each side,
   not a range — so priceLow and priceHigh both get set to the SAME
   averaged value for whichever `priceField` the caller asks for
   ("clientPrice" or "supplierPrice"). This lets the result feed straight
   into holtWintersForecast() unchanged (same { year, weekIso, priceLow,
   priceHigh } contract as aggregateHistoricalPricesByWeek above) while
   still being honest on the chart: the actuals band collapses to a flat
   line (no low/high spread to show) and the forecast's own confidence
   band still comes from the residual variance, not a fake range. Multiple
   real sales the same product/week at different prices (Arturo: "puede
   haber ventas distintas mismo dia") are averaged together like any other
   week with multiple reports — same convention as
   aggregateHistoricalPricesByWeek. */
export function aggregateInternalPriceHistoryByWeek(internalRows, priceField, productFilter, categoryFilter, sizeFilter) {
  var buckets = {};
  (internalRows || []).forEach(function (r) {
    if (!r) return;
    if (productFilter && r.product !== productFilter) return;
    if (categoryFilter && r.category !== categoryFilter) return;
    if (sizeFilter && r.size !== sizeFilter) return;
    if (!r.reportDate) return;
    var price = parseFloat(r[priceField]);
    if (isNaN(price)) return;
    var year = new Date(r.reportDate + "T00:00:00").getFullYear();
    var weekIso = "W" + getISOWeek(r.reportDate);
    var key = year + "|" + weekIso;
    if (!buckets[key]) buckets[key] = { year: year, weekIso: weekIso, prices: [] };
    buckets[key].prices.push(price);
  });
  return Object.keys(buckets)
    .map(function (k) { return buckets[k]; })
    .map(function (b) {
      var avg = round2(average(b.prices));
      return { year: b.year, weekIso: b.weekIso, priceLow: avg, priceHigh: avg };
    })
    .sort(function (a, b) {
      if (a.year !== b.year) return a.year - b.year;
      return a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0;
    });
}
